import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'
import React, { useEffect } from 'react'
import getConfig from 'next/config'
import { ParsedUrlQuery } from 'node:querystring'
import getRawBody from 'raw-body'
import nookies from 'nookies'
import { ApplicationForm } from '../components/ApplicationForm'
import QuestionKey from '../Questions/question-key'
import VehicleService, { VehicleDetailsDataObject } from '../services/vehicle-service'
import $console from '../../packages/utilities/src/$console'
import getSource from '../config/Sources'
import VrnService from '../services/vrn-service'
import { ApplicationFormAnswers, LenderDetails, FeatureSwitches, LoggingSwitches } from '../components/ApplicationForm/ApplicationFormContext'
import Source from '../types/Source'
import ExperimentService from '../services/experiment-service'
import { AttributionDetails } from '../components/ApplicationForm/types'
import { AttributionContext } from '../context'
import { calculateFeatureConfig } from '../utils/features-utils/features-utils'
import { createQuestionConfig } from '../Questions/question-config-factory'
import { fireReactGAEvent, GAEventArgs } from '../components/Questions/utils'
import { PrefillReviewPageApplicationFormProvider } from '../components/ApplicationForm/PrefillReviewPageApplicationFormContext'
import { PrefillReviewPageApplicationForm } from '../components/ApplicationForm/PrefillReviewPageApplicationForm'
import { isAutotraderAppCampaign, isAutotraderAppCampaignForAndroid } from '../utils/detect-device'
import { RequiredDetailsPageApplicationForm } from '../components/ApplicationForm/RequiredDetails/RequiredDetailsPageApplicationForm'
import { FormlessJourney } from '../components/ApplicationForm/FormlessJourney/FormlessJourney'
import EventsService, { AppFormEventParams } from '../services/events-service'
import { mapFormlessApplication, submitFromlessApplication } from '../utils/formless-utils'
import { Application } from '../Mappers/ApplicationFormMapper'
import { getPrefillDetails } from '../utils/getServerSideProps/getPrefillDetails'
import { FormlessJourneyWithContinueCtaWrapper } from '../components/ApplicationForm/FormlessJourney/WithContinueCta/FormlessJourneyWithContinueCTAWrapper'
import { FormlessJourneyV3Wrapper } from '../components/ApplicationForm/FormlessJourney/WithInterstitialAndLandingPage/FormlessJourneyV3Wrapper'
import { extractExperimentAndVariant } from '../utils/experiment-utils/extractExperimentAndVariant'
import { buildV1Attribution, getParameter } from '../utils/utils-cookies'

export type IndexProps = {
  questionConfig: QuestionKey[]
  eventsServiceUrl: string
  customerId: string
  vehicle?: VehicleDetailsDataObject
  source?: Source
  prefillData?: ApplicationFormAnswers
  applicationType: string
  lenderDetails?: LenderDetails
  showRepRate?: boolean
  experimentName: string
  attribution: AttributionDetails
  isBotTraffic: boolean
  isOfficeTraffic: boolean
  journey: string
  featureSwitches: FeatureSwitches
  loggingSwitches: LoggingSwitches
  addressRedesign?: boolean
  submissionResponse?: Record<string, string | boolean>
  mappedApplication?: Application
  appFormEventParams?: AppFormEventParams
}

const Index: NextPage<IndexProps> = ({
  questionConfig,
  vehicle,
  source,
  prefillData,
  applicationType,
  lenderDetails,
  showRepRate,
  eventsServiceUrl,
  customerId,
  experimentName,
  isBotTraffic,
  isOfficeTraffic,
  journey,
  featureSwitches,
  attribution,
  loggingSwitches,
  submissionResponse,
  mappedApplication,
  appFormEventParams
}: IndexProps): JSX.Element => {
  const isEligibleForFormlessJourneyWithContinueCta = attribution.source === 'carmoola' || attribution.source === 'experian'

  useEffect(() => {
    const appFormStartGAEvent: GAEventArgs = {
      category: 'Application Form',
      action: 'Start',
      label: applicationType,
      nonInteraction: true,
      prefillData: !!prefillData
    }

    if (prefillData) {
      appFormStartGAEvent.lenderName = lenderDetails?.lenderName || 'zuto'
    }

    fireReactGAEvent(appFormStartGAEvent)
  }, [])

  const renderApplicationForm = (): JSX.Element => {
    if (featureSwitches.formlessJourneyV1) {
      if (isEligibleForFormlessJourneyWithContinueCta) {
        return (
          <FormlessJourneyWithContinueCtaWrapper
            experimentName={experimentName}
            attribution={attribution}
            submissionResponse={submissionResponse}
            isPreapproved={lenderDetails?.preapproved ?? false}
            eventsServiceUrl={eventsServiceUrl}
            appFormEventParams={appFormEventParams}
            mappedApplication={mappedApplication}
            lenderDetails={lenderDetails}
            shouldShowConfetti={featureSwitches.formlessConfettiV1}
          />
        )
      }
      if (featureSwitches.formlessJourneyV3)
        return (
          <FormlessJourneyV3Wrapper
            experimentName={experimentName}
            attribution={attribution}
            submissionResponse={submissionResponse}
            isPreapproved={lenderDetails?.preapproved ?? false}
            eventsServiceUrl={eventsServiceUrl}
            appFormEventParams={appFormEventParams}
            mappedApplication={mappedApplication}
            lenderDetails={lenderDetails}
          />
        )
      return (
        <FormlessJourney
          experimentName={experimentName}
          attribution={attribution}
          submissionResponse={submissionResponse}
          isPreapproved={lenderDetails?.preapproved ?? false}
          eventsServiceUrl={eventsServiceUrl}
          appFormEventParams={appFormEventParams}
          mappedApplication={mappedApplication}
        />
      )
    }
    if (featureSwitches.requiredDetailsV1) {
      return (
        <RequiredDetailsPageApplicationForm
          config={questionConfig}
          vehicle={prefillData?.vehicleDetails}
          prefillData={prefillData}
          lenderDetails={lenderDetails}
          eventsServiceUrl={eventsServiceUrl}
          customerId={customerId}
          experimentName={experimentName}
          isBotTraffic={isBotTraffic}
          isOfficeTraffic={isOfficeTraffic}
          journey={journey}
          featureSwitches={featureSwitches}
          loggingSwitches={loggingSwitches}
          attribution={attribution}
        />
      )
    }

    if (featureSwitches?.reviewPageV3) {
      return (
        <PrefillReviewPageApplicationFormProvider>
          <PrefillReviewPageApplicationForm
            config={questionConfig}
            vehicle={prefillData?.vehicleDetails}
            prefillData={prefillData}
            lenderDetails={lenderDetails}
            eventsServiceUrl={eventsServiceUrl}
            customerId={customerId}
            experimentName={experimentName}
            isBotTraffic={isBotTraffic}
            isOfficeTraffic={isOfficeTraffic}
            journey={journey}
            featureSwitches={featureSwitches}
            loggingSwitches={loggingSwitches}
            attribution={attribution}
          />
        </PrefillReviewPageApplicationFormProvider>
      )
    }

    return (
      <ApplicationForm
        config={questionConfig}
        vehicle={vehicle}
        source={source}
        prefillData={prefillData}
        lenderDetails={lenderDetails}
        showRepRate={showRepRate}
        eventsServiceUrl={eventsServiceUrl}
        customerId={customerId}
        experimentName={experimentName}
        isBotTraffic={isBotTraffic}
        isOfficeTraffic={isOfficeTraffic}
        journey={journey}
        featureSwitches={featureSwitches}
        loggingSwitches={loggingSwitches}
      />
    )
  }

  return <AttributionContext.Provider value={attribution}>{renderApplicationForm()}</AttributionContext.Provider>
}

const getVRN = (vrn?: string): string | undefined => {
  if (vrn) {
    if (vrn.replace(/\s+/g, '').length > 7) {
      return VrnService.decrypt(vrn)
    }
    return vrn
  }
  return undefined
}

const getVehicleDetailsFromURL = (query: ParsedUrlQuery): VehicleDetailsDataObject | undefined => {
  const vehiclePrice = query.vehiclePrice || query.value
  const advertUrl = query.CarUrl
  if (vehiclePrice) {
    const vehicle: VehicleDetailsDataObject = {
      price: vehiclePrice ? parseInt(vehiclePrice as string, 10) : undefined,
      make: query.make ? (query.make as string) : undefined,
      model: query.model ? (query.model as string) : undefined,
      mileage: query.mileage ? parseInt(query.mileage as string, 10) : undefined,
      registration: getVRN(query.vrn as string),
      imageUrls: query.imageurl
        ? [
            query.imageurl as string
            // replace undefined with placeholder image
          ]
        : undefined,
      dealerName: query.dealer ? (query.dealer as string) : undefined,
      registrationYear: query.regyear ? parseInt((query.regyear as string).substring(0, 4), 10) : undefined,
      type: query.vrn ? 'CAR' : undefined
    }
    if (advertUrl) {
      vehicle.advertUrl = Array.isArray(advertUrl) ? advertUrl[0] : advertUrl
    }
    return vehicle
  }

  return undefined
}

// TODO: Add tests and then split this into multiple functions.
export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<IndexProps>> => {
  const config = {
    eventsApi: process.env.eventsApi,
    zutoAnalyticsUrl: process.env.zutoAnalyticsUrl
  }

  const bodyParams: Record<string, string> = {}
  if (context.req.method === 'POST') {
    const body = (await getRawBody(context.req)).toString('utf-8')
    const pairs = body.split('&')
    pairs.forEach((p) => {
      const [key, value] = p.split('=')
      bodyParams[key] = value
    })
  }

  const {
    zuto_quoteid: zutoQuoteIdCookie,
    zuto_customerid: zutoCustomerIdCookie,
    zuto_advertid: zutoAdvertIdCookie,
    zuto_showRepRate: zutoShowRepRateCookie,
    zuto_wbraid: wbraid,
    zuto_gbraid: gbraid,
    zuto_fbclid: fbclid
  } = nookies.get(context)
  
  const { serverRuntimeConfig } = getConfig()
  const queryAdvertId = context.query.advertId || context.query.advertid
  const advertId = getParameter(queryAdvertId, zutoAdvertIdCookie)

  const deviceId = context.req.headers.device as string
  const sessionId = context.req.headers.session as string
  const isOfficeTraffic = (context.req.headers['z-is-office-traffic'] as string) === '1'
  const isBotTraffic = (context.req.headers['z-is-bot-traffic'] as string) === '1'
  const journey = (context.req.headers['z-journey'] as string) || 'AppForm2021'
  const attribution = buildV1Attribution(context)
  const showRepRate = getParameter(context.query.showRepRate, zutoShowRepRateCookie)

  let customerId = bodyParams?.customerId || getParameter(context.query.customerId, zutoCustomerIdCookie, true)
  let quoteId = bodyParams?.quoteId || getParameter(context.query.quoteId, zutoQuoteIdCookie)

  // MSM bundle the customerId and quoteId inside one query param
  if (customerId?.includes('&')) {
    const [customerIdValue, quoteIdParam] = customerId.split('&')
    customerId = customerIdValue
    const [, quoteIdValue] = quoteIdParam.split('=')
    quoteId = quoteIdValue
  }

  const logManualAddressEntries = await ExperimentService().getIsExperiment(deviceId, 'logging_manual-address-entry-fields')

  const loggingSwitches: LoggingSwitches = {
    logManualAddressEntryFields: logManualAddressEntries
  }

  let vehicle
  let source: Source | undefined

  const prefillDetailsResult = await getPrefillDetails({
    quoteId,
    customerId,
    attribution,
    deviceId
  })

  let applicationType = prefillDetailsResult.applicationType
  const lenderDetails = prefillDetailsResult.lenderDetails
  const prefillData: ApplicationFormAnswers | undefined = prefillDetailsResult.prefillData

  // If we have the AdvertID and a Source, Use the API
  const isClassifiedSource = attribution.source === 'autotrader'

  if (advertId && attribution.source && isClassifiedSource) {
    const vehicleService = VehicleService({
      vehicleURL: serverRuntimeConfig.vehicleURL
    })
    try {
      vehicle = await vehicleService.getVehicle(advertId, attribution.source)
    } catch (e) {
      $console.error(e)
      $console.error('context', context)
    }
  } else {
    // Otherwise, get the details from the URL params provided
    vehicle = getVehicleDetailsFromURL(context.query)
  }

  if (attribution.source) {
    source = getSource(attribution.source)
  }

  if (vehicle) {
    applicationType = 'vehicle advert'
  }

  const featureConfig = await calculateFeatureConfig({
    attribution,
    deviceId,
    lenderDetails,
    prefillData: prefillData,
    comesFromAutotraderApp: isAutotraderAppCampaign(context.query),
    comesFromAutotraderAppAndroidOnly: isAutotraderAppCampaignForAndroid(context.query)
  })

  const questionConfig = createQuestionConfig({
    features: featureConfig.featuresV1,
    lenderDetails,
    vehicle,
    attributionSource: attribution.source,
    prefillData
  })

  $console.info(
    JSON.stringify({
      eventType: 'ApplyPageServed',
      source: attribution.source,
      lender: lenderDetails ? `${lenderDetails.lenderName} - ${lenderDetails?.preapproved}` : 'none',
      questionSet: questionConfig
    })
  )

  const { experimentName, variant } = extractExperimentAndVariant(featureConfig.experimentName)

  context.res.setHeader(
    'Z-Experiments',
    JSON.stringify([
      {
        ExperimentName: experimentName,
        Variant: variant
      }
    ])
  )

  // isElegibleForFormlessJourney when experiment is on
  const isElegibleForFormlessJourney = featureConfig.featuresV1.formlessJourneyV1

  if (isElegibleForFormlessJourney && prefillData) {
    // appform started
    const appFormEventParams = {
      Lender: lenderDetails?.lenderName,
      PreApproved: lenderDetails?.preapproved,
      QuoteId: lenderDetails?.quoteId,
      CustomerId: customerId,
      PartnerCustomerId: prefillData?.partnerCustomerId,
      Prefill: !!prefillData,
      DeviceId: deviceId,
      SessionId: sessionId,
      Experiments: featureConfig.experimentName,
      Journey: journey,
      Attribution: attribution,
      Wbraid: wbraid,
      Gbraid: gbraid,
      Fbclid: fbclid
    }
    const eventsService = EventsService(config.eventsApi, appFormEventParams)
    void eventsService.sendAppFormStartedEvent().catch((err) => $console.error('sendAppFormStartedEvent failed', err))

    const metaData = {
      isBotTraffic,
      isOfficeTraffic,
      customerId,
      partnerCustomerId: prefillData?.partnerCustomerId,
      deviceId,
      sessionId,
      lender: lenderDetails?.lenderName,
      isPrefill: true,
      isPreapproved: lenderDetails?.preapproved,
      isRetry: false,
      experiments: featureConfig.experimentName,
      journey: journey,
      wbraid: appFormEventParams.Wbraid,
      gbraid: appFormEventParams.Gbraid,
      fbclid: appFormEventParams.Fbclid
    }
   
    const mappedApplication = mapFormlessApplication({
      prefillData,
      attribution,
      experimentName: featureConfig.experimentName,
      metaData,
      quoteId: lenderDetails?.quoteId,
      query: context.query
    })

    // submit app
    const submissionResponse = await submitFromlessApplication({
      application: mappedApplication,
      eventsService
    })

    // props
    const typedProps: IndexProps = {
      questionConfig,
      vehicle,
      source,
      prefillData,
      applicationType,
      lenderDetails,
      showRepRate: showRepRate === 'true' ? true : source?.showRepresentativeExample,
      eventsServiceUrl: config.eventsApi,
      customerId,
      experimentName: featureConfig.experimentName,
      attribution,
      isOfficeTraffic,
      isBotTraffic,
      journey,
      featureSwitches: featureConfig.featuresV1,
      loggingSwitches,
      submissionResponse,
      mappedApplication,
      appFormEventParams
    }

    return {
      props: JSON.parse(JSON.stringify(typedProps))
    }
  }

  const typedProps: IndexProps = {
    questionConfig,
    vehicle,
    source,
    prefillData,
    applicationType,
    lenderDetails,
    showRepRate: showRepRate === 'true' ? true : source?.showRepresentativeExample,
    eventsServiceUrl: config.eventsApi,
    customerId,
    experimentName: featureConfig.experimentName,
    attribution,
    isOfficeTraffic,
    isBotTraffic,
    journey,
    featureSwitches: featureConfig.featuresV1,
    loggingSwitches
  }

  return {
    props: JSON.parse(JSON.stringify(typedProps))
  }
}

export default Index
